import { LegalDocuments } from "@capitalos/react";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { type } from "arktype";
import {
  CreditCard,
  Crown,
  Loader2,
  MapPin,
  Pencil,
  Plus,
  Trash2,
  Truck,
} from "lucide-react";
import { Suspense } from "react";
import {
  createAddressValidator,
  editOrg,
  stateOptions,
  updateAddressValidator,
  updateBillingAddressValidator,
} from "shared";
import { toast } from "sonner";
import type z from "zod";
import {
  AccountActionsLoading,
  AccountActionWrapper,
} from "@/components/account-action-wrapper";
import { IconText } from "@/components/icon-text";
import { Page } from "@/components/page";
import { SheetSkeleton } from "@/components/skeletons/sheet-skeleton";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useAppForm } from "@/hooks/useCreateForm";
import { restrictRoute } from "@/lib/routeBlocker";

const validateSearch = type({
  "editBilling?": "boolean",
  "editShipping?": "string",
  "newAddress?": "boolean",
});

export const Route = createFileRoute("/_authed/_org/organization/settings/")({
  beforeLoad: async ({ context }) =>
    restrictRoute(context, {
      redirect: {
        to: "/organization/people",
      },
      requiredRole: "admin",
    }),
  component: RouteComponent,
  validateSearch,
});

function formatAddress(addr: {
  addressLine1: string;
  addressLine2?: string | null;
  city: string;
  state: string;
  zipcode: string;
}) {
  return `${addr.addressLine1}${addr.addressLine2 ? `, ${addr.addressLine2}` : ""}, ${addr.city}, ${addr.state} ${addr.zipcode}`;
}

function RouteComponent() {
  const { trpc, queryClient } = Route.useRouteContext();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const { data: sessionData } = useSuspenseQuery(
    trpc.auth.session.queryOptions(),
  );
  const { data: billingAddress } = useSuspenseQuery(
    trpc.org.getBillingAddress.queryOptions(),
  );
  const { data: shippingData } = useSuspenseQuery(
    trpc.org.getShippingAddresses.queryOptions(),
  );

  const { mutate: editOrgMutation, isPending } = useMutation(
    trpc.org.edit.mutationOptions({
      onError: (error) => {
        toast.error(error.message);
      },
      onSuccess: () => {
        toast.success("Organization settings updated successfully");
        queryClient.invalidateQueries(trpc.auth.session.queryOptions());
        queryClient.invalidateQueries(trpc.org.addresses.queryOptions());
        form.reset();
      },
    }),
  );

  const primaryShippingAddress = shippingData.addresses.find(
    (addr) => addr.id === shippingData.primaryShippingAddressId,
  );

  const form = useAppForm({
    defaultValues: {
      dbaName: sessionData?.org?.dbaName ?? null,
      defaultAddressId: primaryShippingAddress?.id || "",
      enableTransparency:
        sessionData?.org?.features.includes("transparency") ?? false,
    },
    onSubmit: ({ value }) => editOrgMutation(value),
    validators: {
      onSubmit: editOrg,
    },
  });

  const closeSheet = () => {
    navigate({
      search: {},
    });
  };

  const isSheetOpen =
    search.editBilling === true ||
    search.newAddress === true ||
    !!search.editShipping;

  return (
    <Page heading="Organization Settings">
      <div className="mb-4 flex h-full w-full max-w-3xl flex-col gap-6 self-center p-6">
        <form.AppForm>
          <form
            className="space-y-2"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
          >
            <form.AppField
              children={(field) => (
                <field.TextField
                  description="Your organization's 'Doing Business As' name"
                  label="DBA Name"
                  placeholder="DBA Name"
                  required={false}
                  className="mb-4"
                />
              )}
              name="dbaName"
            />

            <div className="mt-8 flex items-center justify-between">
              <div className="flex max-w-3xl grow flex-col gap-1">
                <h2 className="font-medium text-base text-neutral-800">
                  Publish our finances publicly
                </h2>

                <h4 className="max-w-prose text-neutral-500 text-xs">
                  Givefront can set up a public page where others can see recent
                  transactions and other financial data in your organization for
                  transparency.
                </h4>
              </div>

              <form.AppField name="enableTransparency">
                {(field) => <field.SwitchField />}
              </form.AppField>
            </div>

            {sessionData?.org?.features.includes("transparency") && (
              <IconText icon="eye" className="mt-2 text-sm">
                We've set up a public dashboard for your finances{" "}
                <Link
                  to="/transparency/$orgId"
                  params={{ orgId: sessionData.org.id }}
                  className="text-gray-500 underline"
                  target="_blank"
                  rel="noopener"
                >
                  here
                </Link>
              </IconText>
            )}

            <div className="mt-4 flex gap-2">
              <form.ConditionalSubmitCancelButtons isPending={isPending} />
            </div>
          </form>
        </form.AppForm>

        <div className="my-6 h-px w-full bg-border" />

        <h2 className="font-semibold text-lg">Addresses</h2>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="flex items-center gap-2 font-medium text-base">
              <CreditCard className="h-4 w-4" />
              Billing Address
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                navigate({
                  search: { editBilling: true },
                })
              }
            >
              <Pencil className="mr-1 h-3 w-3" />
              Edit
            </Button>
          </CardHeader>
          <CardContent>
            {billingAddress ? (
              <p className="text-muted-foreground text-sm">
                {formatAddress(billingAddress)}
              </p>
            ) : (
              <p className="text-muted-foreground text-sm">
                No billing address set
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="flex items-center gap-2 font-medium text-base">
              <Truck className="h-4 w-4" />
              Shipping Addresses
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                navigate({
                  search: { newAddress: true },
                })
              }
            >
              <Plus className="mr-1 h-3 w-3" />
              Add
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {shippingData.addresses.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                No shipping addresses set
              </p>
            ) : (
              shippingData.addresses.map((addr) => (
                <div
                  key={addr.id}
                  className="flex items-start justify-between rounded-md border p-3"
                >
                  <div className="flex items-start gap-2">
                    <MapPin className="mt-0.5 h-4 w-4 text-muted-foreground" />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">
                          {addr.name || "Unnamed Address"}
                        </span>
                        {addr.id === shippingData.primaryShippingAddressId && (
                          <span className="flex items-center gap-1 text-amber-600 text-xs">
                            <Crown className="h-3 w-3" />
                            Primary
                          </span>
                        )}
                      </div>
                      <p className="text-muted-foreground text-sm">
                        {formatAddress(addr)}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      navigate({
                        search: { editShipping: addr.id },
                      })
                    }
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <div className="flex flex-col items-start justify-start">
          <AccountActionWrapper className="mb-48">
            <LegalDocuments
              loadingComponent={<AccountActionsLoading />}
              onDone={() => {}}
            />
          </AccountActionWrapper>
        </div>
      </div>

      <Sheet open={isSheetOpen} onOpenChange={closeSheet}>
        <SheetContent>
          <Suspense fallback={<SheetSkeleton />}>
            <div className="flex h-full flex-col pb-4">
              {search.editBilling === true && (
                <EditBillingAddressSheet billingAddress={billingAddress!} />
              )}
              {search.newAddress === true && <NewAddressSheet />}
              {search.editShipping && (
                <EditShippingAddressSheet
                  addressId={search.editShipping}
                  primaryShippingAddressId={
                    shippingData.primaryShippingAddressId
                  }
                />
              )}
            </div>
          </Suspense>
        </SheetContent>
      </Sheet>
    </Page>
  );
}

function EditBillingAddressSheet({
  billingAddress,
}: {
  billingAddress: {
    addressLine1: string;
    addressLine2?: string | null;
    city: string;
    state: string;
    zipcode: string;
  } | null;
}) {
  const { trpc, queryClient } = Route.useRouteContext();
  const navigate = Route.useNavigate();

  const { mutate: updateBilling, isPending } = useMutation(
    trpc.org.updateBillingAddress.mutationOptions({
      onError: (error) => {
        toast.error(error.message);
      },
      onSuccess: () => {
        toast.success("Billing address updated");
        queryClient.invalidateQueries(
          trpc.org.getBillingAddress.queryOptions(),
        );
        navigate({ search: {} });
      },
    }),
  );

  const form = useAppForm({
    defaultValues: {
      address: {
        addressLine1: billingAddress?.addressLine1 ?? "",
        addressLine2: (billingAddress?.addressLine2 ?? "") as string | null,
        city: billingAddress?.city ?? "",
        state: billingAddress?.state ?? "",
        zipcode: billingAddress?.zipcode ?? "",
      },
    } as z.infer<typeof updateBillingAddressValidator>,
    onSubmit: ({ value }) => updateBilling(value),
    validators: {
      onSubmit: updateBillingAddressValidator,
    },
  });

  return (
    <>
      <SheetHeader className="pb-4">
        <SheetTitle>Edit Billing Address</SheetTitle>
        <SheetDescription>
          This address will be used for billing & invoicing on Givefront.
        </SheetDescription>
      </SheetHeader>

      <form
        className="flex flex-1 flex-col space-y-4 overflow-y-auto px-6"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <form.AppField
          name="address.addressLine1"
          children={(field) => (
            <field.TextField label="Address Line 1" required={true} />
          )}
        />
        <form.AppField
          name="address.addressLine2"
          children={(field) => (
            <field.TextField label="Address Line 2" required={false} />
          )}
        />
        <form.AppField
          name="address.city"
          children={(field) => <field.TextField label="City" required={true} />}
        />
        <div className="flex gap-4">
          <form.AppField
            name="address.state"
            children={(field) => (
              <field.SelectField
                label="State"
                options={stateOptions}
                required={true}
              />
            )}
          />
          <form.AppField
            name="address.zipcode"
            children={(field) => (
              <field.TextField label="Zipcode" required={true} type="number" />
            )}
          />
        </div>

        <div className="mt-auto flex gap-2 border-t pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate({ search: {} })}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        </div>
      </form>
    </>
  );
}

function NewAddressSheet() {
  const { trpc, queryClient } = Route.useRouteContext();
  const navigate = Route.useNavigate();

  const { mutate: createAddress, isPending } = useMutation(
    trpc.org.createAddress.mutationOptions({
      onError: (error) => {
        toast.error(error.message);
      },
      onSuccess: () => {
        toast.success("Address created");
        queryClient.invalidateQueries(
          trpc.org.getShippingAddresses.queryOptions(),
        );
        queryClient.invalidateQueries(trpc.org.addresses.queryOptions());
        navigate({ search: {} });
      },
    }),
  );

  const form = useAppForm({
    defaultValues: {
      address: {
        addressLine1: "",
        addressLine2: "" as string | null,
        city: "",
        name: "" as string | null,
        state: "" as any,
        zipcode: "",
      },
    } as z.infer<typeof createAddressValidator>,
    onSubmit: ({ value }) => createAddress(value),
    validators: {
      onSubmit: createAddressValidator,
    },
  });

  return (
    <div className="flex h-full flex-col">
      <SheetHeader className="pb-4">
        <SheetTitle>Add Shipping Address</SheetTitle>
        <SheetDescription>
          Add a new shipping address for your organization.
        </SheetDescription>
      </SheetHeader>

      <form
        className="flex flex-1 flex-col space-y-4 overflow-y-auto px-6"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <form.AppField
          name="address.name"
          children={(field) => (
            <field.TextField
              label="Address Name"
              placeholder="e.g. Main Office, Warehouse"
              required={false}
            />
          )}
        />
        <form.AppField
          name="address.addressLine1"
          children={(field) => (
            <field.TextField label="Address Line 1" required={true} />
          )}
        />
        <form.AppField
          name="address.addressLine2"
          children={(field) => (
            <field.TextField label="Address Line 2" required={false} />
          )}
        />
        <form.AppField
          name="address.city"
          children={(field) => <field.TextField label="City" required={true} />}
        />
        <div className="flex gap-4">
          <form.AppField
            name="address.state"
            children={(field) => (
              <field.SelectField
                label="State"
                options={stateOptions}
                required={true}
              />
            )}
          />
          <form.AppField
            name="address.zipcode"
            children={(field) => (
              <field.TextField label="Zipcode" required={true} type="number" />
            )}
          />
        </div>

        <div className="mt-auto flex gap-2 border-t pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate({ search: {} })}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Add Address
          </Button>
        </div>
      </form>
    </div>
  );
}

function EditShippingAddressSheet({
  addressId,
  primaryShippingAddressId,
}: {
  addressId: string;
  primaryShippingAddressId: string | null;
}) {
  const { trpc, queryClient } = Route.useRouteContext();
  const navigate = Route.useNavigate();

  const { data: shippingData } = useSuspenseQuery(
    trpc.org.getShippingAddresses.queryOptions(),
  );

  const address = shippingData.addresses.find((a) => a.id === addressId);

  const { mutate: updateAddress, isPending: isUpdating } = useMutation(
    trpc.org.updateAddress.mutationOptions({
      onError: (error) => {
        toast.error(error.message);
      },
      onSuccess: () => {
        toast.success("Address updated");
        queryClient.invalidateQueries(
          trpc.org.getShippingAddresses.queryOptions(),
        );
        queryClient.invalidateQueries(trpc.org.addresses.queryOptions());
        navigate({ search: {} });
      },
    }),
  );

  const { mutate: deleteAddress, isPending: isDeleting } = useMutation(
    trpc.org.deleteAddress.mutationOptions({
      onError: (error) => {
        toast.error(error.message);
      },
      onSuccess: () => {
        toast.success("Address deleted");
        queryClient.invalidateQueries(
          trpc.org.getShippingAddresses.queryOptions(),
        );
        queryClient.invalidateQueries(trpc.org.addresses.queryOptions());
        navigate({ search: {} });
      },
    }),
  );

  const { mutate: setPrimary, isPending: isSettingPrimary } = useMutation(
    trpc.org.setPrimaryShippingAddress.mutationOptions({
      onError: (error) => {
        toast.error(error.message);
      },
      onSuccess: () => {
        toast.success("Primary shipping address updated");
        queryClient.invalidateQueries(
          trpc.org.getShippingAddresses.queryOptions(),
        );
        queryClient.invalidateQueries(trpc.org.addresses.queryOptions());
      },
    }),
  );

  const form = useAppForm({
    defaultValues: {
      address: {
        addressLine1: address?.addressLine1 ?? "",
        addressLine2: address?.addressLine2 ?? "",
        city: address?.city ?? "",
        name: address?.name ?? "",
        state: address?.state ?? "",
        zipcode: address?.zipcode ?? "",
      },
      addressId,
    } as z.infer<typeof updateAddressValidator>,
    onSubmit: ({ value }) => updateAddress(value),
    validators: {
      onSubmit: updateAddressValidator,
    },
  });

  const isPrimary = addressId === primaryShippingAddressId;
  const canDelete = !isPrimary && shippingData.addresses.length > 1;
  const isPending = isUpdating || isDeleting || isSettingPrimary;

  if (!address) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Address not found</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <SheetHeader className="pb-4">
        <SheetTitle>Edit Shipping Address</SheetTitle>
        <SheetDescription>
          Update or manage this shipping address.
        </SheetDescription>
      </SheetHeader>

      <form
        className="flex flex-1 flex-col space-y-4 overflow-y-auto px-6"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <form.AppField
          name="address.name"
          children={(field) => (
            <field.TextField
              label="Address Name"
              placeholder="e.g. Main Office, Warehouse"
              required={false}
            />
          )}
        />
        <form.AppField
          name="address.addressLine1"
          children={(field) => (
            <field.TextField label="Address Line 1" required={true} />
          )}
        />
        <form.AppField
          name="address.addressLine2"
          children={(field) => (
            <field.TextField label="Address Line 2" required={false} />
          )}
        />
        <form.AppField
          name="address.city"
          children={(field) => <field.TextField label="City" required={true} />}
        />
        <div className="flex gap-4">
          <form.AppField
            name="address.state"
            children={(field) => (
              <field.SelectField
                label="State"
                options={stateOptions}
                required={true}
              />
            )}
          />
          <form.AppField
            name="address.zipcode"
            children={(field) => (
              <field.TextField label="Zipcode" required={true} type="number" />
            )}
          />
        </div>

        {isPrimary ? (
          <div className="flex items-center gap-2 rounded-md bg-amber-50 p-3 text-amber-800 text-sm">
            <Crown className="h-4 w-4" />
            This is your primary shipping address
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            onClick={() => setPrimary({ addressId })}
            disabled={isPending}
          >
            {isSettingPrimary && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            <Crown className="mr-2 h-4 w-4" />
            Set as Primary
          </Button>
        )}

        <div className="mt-auto flex justify-between border-t pt-4">
          <div>
            {canDelete && (
              <Button
                type="button"
                variant="destructive"
                onClick={() => deleteAddress({ addressId })}
                disabled={isPending}
              >
                {isDeleting ? (
                  <Loader2 className="mr-2 size-4 animate-spin" />
                ) : (
                  <Trash2 className="mr-2 size-4" />
                )}
                Delete
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate({ search: {} })}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
